import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useProfiles } from '@jwp/ott-hooks-react/src/useProfiles';
import ErrorPage from '@jwp/ott-ui-react/src/components/ErrorPage/ErrorPage';
import RootErrorPage from '@jwp/ott-ui-react/src/components/RootErrorPage/RootErrorPage';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';
import About from '@jwp/ott-ui-react/src/pages/About/About';
import ActivateTV from '@jwp/ott-ui-react/src/pages/ActivateTV/ActivateTV';
import Search from '@jwp/ott-ui-react/src/pages/Search/Search';
import User from '@jwp/ott-ui-react/src/pages/User/User';
import FavouritesPage from '@jwp/ott-ui-react/src/pages/FavouritesPage/FavouritesPage';
import SeeMoreItemsPage from '@jwp/ott-ui-react/src/pages/SeeMoreItemsPage/SeeMoreItemsPage';
import LegacySeries from '@jwp/ott-ui-react/src/pages/LegacySeries/LegacySeries';
import MyVideosPage from '@jwp/ott-ui-react/src/pages/MyVideosPage/MyVideosPage';
import MediaScreenRouter from '@jwp/ott-ui-react/src/pages/ScreenRouting/MediaScreenRouter';
import PlaylistScreenRouter from '@jwp/ott-ui-react/src/pages/ScreenRouting/PlaylistScreenRouter';
import Layout from '@jwp/ott-ui-react/src/containers/Layout/Layout';
import Profiles from '@jwp/ott-ui-react/src/containers/Profiles/Profiles';
import CreateProfile from '@jwp/ott-ui-react/src/containers/Profiles/CreateProfile';
import EditProfile from '@jwp/ott-ui-react/src/containers/Profiles/EditProfile';
import VideoPreviewPage from '@jwp/ott-ui-react/src/pages/VideoPreviewPage/VideoPreviewPage';
import MyVideoDetailsPage from '@jwp/ott-ui-react/src/pages/MyVideoDetailsPage/MyVideoDetailsPage';
import ModerateContent from '@jwp/ott-ui-react/src/components/ModerateContent/ModerateContent';
import VerticalPlayer from '@jwp/ott-ui-react/src/components/VerticalPlayer/VerticalPlayer';
import {
  PATH_ABOUT,
  PATH_LEGACY_SERIES,
  PATH_MEDIA,
  PATH_PLAYLIST,
  PATH_SIGN_IN,
  PATH_FORGOT_PASSWORD,
  PATH_RESET_PASSWORD,
  PATH_USER_PROFILES,
  PATH_USER_PROFILES_CREATE,
  PATH_USER_PROFILES_EDIT,
  PATH_USER_PROFILES_EDIT_PROFILE,
  PATH_SEARCH,
  PATH_USER,
  RELATIVE_PATH_USER_FAVORITES,
  PATH_USER_VIDEOS,
  RELATIVE_PATH_USER_SEEMORE_ITEMS,
  PATH_ACTIVATETV,
  RELATIVE_PATH_USER_TERMS_OF_USE,
  RELATIVE_PATH_USER_PRIVACY_POLICY,
  RELATIVE_PATH_CHECKOUT,
  PATH_VIDEO_PREVIEW,
  PATH_VIDEO_PREVIEW_WITH_ID,
  PATH_MY_VIDEO_DETAILS_WITH_ID,
  PATH_USER_MODERATE,
  PATH_REELS,
  PATH_HOME,
} from '@jwp/ott-common/src/paths';

import useNotifications from '#src/hooks/useNotifications';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import TermsOfUse from '@jwp/ott-ui-react/src/components/TermsOfUse/TermsOfUse';
import PrivacyPolicy from '@jwp/ott-ui-react/src/components/PrivacyPolicy/PrivacyPolicy';

import styles from './AppRoutes.module.scss';
import { CleengCheckout } from '@jwp/ott-ui-react/src/containers/Checkout/CleengCheckout';
import SignIn from '@jwp/ott-ui-react/src/components/SignIn/SignIn';
import ForgotPassword from '@jwp/ott-ui-react/src/components/ForgotPassword/ForgotPassword';
import ResetPassword from '@jwp/ott-ui-react/src/components/ResetPassword/ResetPassword';
import Home from '@jwp/ott-ui-react/src/pages/Home/Home';
import ProtectedRoute from '@jwp/ott-ui-react/src/containers/ProtectedRoute/ProtectedRoute';
import Toast from '@jwp/ott-ui-react/src/components/Toast/Toast';

export default function AppRoutes() {
  const location = useLocation();

  const { t } = useTranslation('error');
  const userModal = useQueryParam('u');

  const { isSwitchProfileEnable, isFavoriteEnable, isUploadEnabled, isActivateTV, isSearchEnabled, canShowPrivacyPolicy, canShowTermsOfUse, autoplayDefaultValue, isAutoPlay } =
    useAppConfig();

  const { accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const user = useAccountStore(({ user }) => user, shallow);
  const { profile, selectingProfileAvatar } = useProfileStore();
  const { profilesEnabled } = useProfiles();

  useEffect(() => {
    if (!localStorage.getItem('autoplay') && isAutoPlay) {
      localStorage.setItem('autoplay', String(autoplayDefaultValue?.toLocaleLowerCase() == 'on'));
    }
  }, []);

  const userData = useAccountStore((s) => ({ loading: s.loading, user: s.user }));

  // listen to websocket notifications
  useNotifications();

  if (userData.user && !userData.loading && window.location.href.includes('#token')) {
    return <Navigate to="/" />; // component instead of hook to prevent extra re-renders
  }

  if (userData.user && selectingProfileAvatar !== null) {
    return <LoadingOverlay profileImageUrl={selectingProfileAvatar || profile?.avatar_url} />;
  }

  const shouldManageProfiles =
    !!user &&
    profilesEnabled &&
    !profile &&
    (accessModel === 'SVOD' || accessModel === 'AUTHVOD') &&
    !userModal &&
    isSwitchProfileEnable &&
    !location.pathname.includes(PATH_USER_PROFILES);

    if (shouldManageProfiles) {
    return <Navigate to={PATH_USER_PROFILES} />;
  }

  return (
    <>
      <Routes>
        <Route path={PATH_SIGN_IN} element={<SignIn />} />
        <Route path={PATH_FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={PATH_RESET_PASSWORD} element={<ResetPassword />} />
        <Route element={<ProtectedRoute />}>
          <Route path={PATH_USER_PROFILES} element={<Profiles />} />
          <Route path={PATH_USER_PROFILES_CREATE} element={<CreateProfile />} />
          <Route path={PATH_USER_PROFILES_EDIT} element={<Profiles editMode />} />
          <Route path={PATH_USER_PROFILES_EDIT_PROFILE} element={<EditProfile />} />
          <Route path={PATH_VIDEO_PREVIEW} element={<VideoPreviewPage />} />
          <Route path={PATH_VIDEO_PREVIEW_WITH_ID} element={<VideoPreviewPage />} />
          <Route path={PATH_REELS} element={<VerticalPlayer />} />
          <Route path={RELATIVE_PATH_CHECKOUT} element={<CleengCheckout />} />
          <Route element={<Layout />} errorElement={<RootErrorPage />}>
            <Route index element={<Home />} />
            <Route path={PATH_HOME} element={<Home />} />
            <Route path={PATH_PLAYLIST} element={<PlaylistScreenRouter />} />
            <Route path={PATH_MEDIA} element={<MediaScreenRouter />} />
            <Route path={PATH_LEGACY_SERIES} element={<LegacySeries />} />
            {isSearchEnabled && <Route path={PATH_SEARCH} element={<Search />} />}
            {isFavoriteEnable && <Route path={RELATIVE_PATH_USER_FAVORITES} element={<FavouritesPage />} />}
            {isUploadEnabled && <Route path={PATH_USER_VIDEOS} element={<MyVideosPage />} />}
            <Route path={PATH_MY_VIDEO_DETAILS_WITH_ID} element={<MyVideoDetailsPage />} />
            {isUploadEnabled && user?.role === 'superadmin' && <Route path={PATH_USER_MODERATE} element={<ModerateContent />} />}
            <Route path={RELATIVE_PATH_USER_SEEMORE_ITEMS} element={<SeeMoreItemsPage />} />
            <Route path={PATH_USER} element={<User />} />
            <Route path={PATH_ABOUT} element={<About />} />
            {isActivateTV && <Route path={PATH_ACTIVATETV} element={<ActivateTV />} />}
            {canShowTermsOfUse && <Route path={RELATIVE_PATH_USER_TERMS_OF_USE} element={<TermsOfUse panelClassName={styles.panel} />} />}
            {canShowPrivacyPolicy && <Route path={RELATIVE_PATH_USER_PRIVACY_POLICY} element={<PrivacyPolicy panelClassName={styles.panel} />} />}

            <Route
              path="/*"
              element={<ErrorPage title={t('notfound_error_heading', 'Not found')} message={t('notfound_error_description', "This page doesn't exist.")} />}
            />
          </Route>
        </Route>
        {/* <Route path={PATH_APPLE_FILE} element={<AppleFile />} /> */}
      </Routes>
      <Toast />
    </>
  );
}
